import {Box, IconButton, Paper, Skeleton, Stack, SxProps, Theme, Typography} from "@mui/material";
import * as React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {dimmedWhile, InsightsLoading} from "./insightsLoading";
import {InsightsTooltip} from "./InsightsTooltip";
import {CHART_HEIGHT} from "./insightsChart";

const HEADLINE_HEIGHT = 32;
const HEADLINE_GAP = 1.5;
const FOOTER_GAP = 2.5;

interface Props {
    title: string;
    hint?: string;
    headline?: React.ReactNode;
    actions?: React.ReactNode;
    footer?: React.ReactNode;
    loading?: InsightsLoading;
    skeletonHeight?: number;
    bodySx?: SxProps<Theme>;
    children: React.ReactNode;
}

/** Base container card with title row, headline slot, actions slot, and footer. */
export function InsightsCard({
    title,
    hint,
    headline,
    actions,
    footer,
    loading,
    skeletonHeight = CHART_HEIGHT,
    bodySx,
    children
}: Props) {
    const skeleton = loading === "skeleton";
    const dimmed = dimmedWhile(loading);

    return (
        <Paper
            component={"section"}
            variant={"outlined"}
            aria-label={title}
            sx={{
                width: "100%",
                minWidth: 0,
                // Fills the column so it does not stop half way up beside a taller card.
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 2.5,
                borderRadius: 2,
                borderColor: "divider",
                bgcolor: "background.paper",
                boxSizing: "border-box"
            }}
        >
            <Stack
                direction={"row"}
                alignItems={"flex-start"}
                justifyContent={"space-between"}
                gap={2}
                sx={{minWidth: 0, mb: 1.5}}
            >
                <Box sx={{minWidth: 0}}>
                    <Stack direction={"row"} alignItems={"center"} gap={0.25} sx={{minWidth: 0, minHeight: "32px"}}>
                        <Typography variant={"body1"} color={"text.secondary"} fontWeight={600} noWrap>
                            {title}
                        </Typography>

                        {!!hint && (
                            <InsightsTooltip placement={"top"} title={hint}>
                                <IconButton
                                    size={"small"}
                                    aria-label={`${title} information`}
                                    sx={{color: "text.disabled", flexShrink: 0, p: 0.5}}
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 18}}/>
                                </IconButton>
                            </InsightsTooltip>
                        )}
                    </Stack>

                    {headline != null && (
                        <Box sx={{mt: HEADLINE_GAP, minHeight: HEADLINE_HEIGHT, display: "flex", alignItems: "center", ...dimmed}}>
                            {skeleton ? <Skeleton animation={"wave"} variant={"rounded"} width={120} height={32}/> : headline}
                        </Box>
                    )}
                </Box>

                {actions != null && (
                    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{flexShrink: 0}}>
                        {actions}
                    </Stack>
                )}
            </Stack>

            <Box sx={[{minWidth: 0, ...dimmed}, ...(Array.isArray(bodySx) ? bodySx : [bodySx])]}>
                {skeleton ? (
                    <Skeleton
                        animation={"wave"}
                        variant={"rounded"}
                        sx={{width: "100%", height: skeletonHeight, borderRadius: 2}}
                    />
                ) : (
                    children
                )}
            </Box>

            {footer != null && (
                <Box sx={{mt: "auto", pt: FOOTER_GAP, ...dimmed}}>
                    {skeleton ? (
                        <Stack direction={"row"} justifyContent={"center"} alignItems={"center"} spacing={2.5} sx={{height: 20}}>
                            <Skeleton animation={"wave"} variant={"rounded"} width={96} height={14}/>
                            <Skeleton animation={"wave"} variant={"rounded"} width={112} height={14}/>
                            <Skeleton animation={"wave"} variant={"rounded"} width={72} height={14}/>
                        </Stack>
                    ) : (
                        footer
                    )}
                </Box>
            )}
        </Paper>
    );
}
